import React from 'react'

import {
  ARROW_HALF,
  LOOP_ARROWS,
  LOOP_NODES,
  NODE_RADIUS,
  NODE_RING_RADIUS,
  RING_CENTER,
  RING_RADIUS,
} from '../../constants/valueLoopDiagramGeometry'

const getMultilineParts = title => {
  if (title.includes(' & ')) return title.split(' & ')
  if (title.includes(' e ')) return title.split(' e ')
  return [title]
}

const getMultilinePrefix = title => (title.includes(' e ') ? 'e ' : '& ')

const CENTER_SUBTITLE_WIDTH = 200
const CENTER_SUBTITLE_X = RING_CENTER - CENTER_SUBTITLE_WIDTH / 2

const ValueLoopDiagram = ({
  steps,
  centerLabel,
  centerSubtitle,
  variant = 'accent',
  activeIndex = -1,
  interactive = false,
  onHoverSelect,
  onSelect,
  className = '',
}) => {
  const rootClass =
    'value-loop-diagram value-loop-diagram--' +
    variant +
    (className ? ' ' + className : '')

  return (
    <div className={rootClass} aria-hidden={!interactive}>
      <svg className="value-loop-diagram__svg" viewBox="0 0 420 420" role="presentation">
        <circle
          className="value-loop-diagram__ring-bg"
          cx={RING_CENTER}
          cy={RING_CENTER}
          r={RING_RADIUS}
        />
        <circle
          className="value-loop-diagram__ring"
          cx={RING_CENTER}
          cy={RING_CENTER}
          r={RING_RADIUS}
        />
        {LOOP_ARROWS.map((arrow, index) => (
          <g
            key={`arrow-${index}`}
            className="value-loop-diagram__arrow"
            transform={`translate(${arrow.x}, ${arrow.y}) rotate(${arrow.rotation}) translate(${-ARROW_HALF}, ${-ARROW_HALF})`}
          >
            <path
              d="M1.33398 1.33398H12.6477V12.6477"
              fill="none"
              strokeWidth="2.66667"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
        ))}
        <text className="value-loop-diagram__center" x="210" y={centerSubtitle ? 207 : 217} textAnchor="middle">
          {centerLabel}
        </text>
        {centerSubtitle ? (
          <foreignObject
            x={CENTER_SUBTITLE_X}
            y="214"
            width={CENTER_SUBTITLE_WIDTH}
            height="80"
            className="value-loop-diagram__center-sub-wrap"
          >
            <p
              xmlns="http://www.w3.org/1999/xhtml"
              className="value-loop-diagram__center-sub"
            >
              {Array.isArray(centerSubtitle) ? centerSubtitle.join(' ') : centerSubtitle}
            </p>
          </foreignObject>
        ) : null}
        {steps.map((step, index) => {
          const node = LOOP_NODES[index]
          const isActive = index === activeIndex
          const labelX = node.labelX
          const labelY = node.labelY
          const stepClass =
            'value-loop-diagram__step' + (isActive ? ' value-loop-diagram__step--active' : '')

          const nodeInner = (
            <>
              <circle
                className="value-loop-diagram__node-ring"
                cx={node.cx}
                cy={node.cy}
                r={NODE_RING_RADIUS}
              />
              <circle
                className="value-loop-diagram__node-bg"
                cx={node.cx}
                cy={node.cy}
                r={NODE_RADIUS}
              />
              <text
                className="value-loop-diagram__node-num"
                x={node.cx}
                y={node.cy + 6}
                textAnchor="middle"
              >
                {String(index + 1).padStart(2, '0')}
              </text>
            </>
          )

          return (
            <g
              key={step.id}
              className={stepClass}
              onMouseEnter={() => {
                if (onHoverSelect) onHoverSelect(index)
              }}
            >
              {interactive ? (
                <g
                  className={
                    'value-loop-diagram__node' +
                    (isActive ? ' value-loop-diagram__node--active' : '')
                  }
                  tabIndex={0}
                  role="button"
                  aria-label={`${step.title}, step ${index + 1}`}
                  onClick={() => {
                    if (onSelect) onSelect(index)
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      if (onSelect) onSelect(index)
                    }
                  }}
                >
                  {nodeInner}
                </g>
              ) : (
                <g className="value-loop-diagram__node">{nodeInner}</g>
              )}
              {node.multiline || step.multiline ? (
                <text
                  className="value-loop-diagram__node-label"
                  x={labelX}
                  y={labelY}
                  textAnchor={node.labelAnchor}
                >
                  {getMultilineParts(step.title).map((line, lineIndex) => (
                    <tspan key={line} x={labelX} dy={lineIndex === 0 ? 0 : 20}>
                      {lineIndex > 0 ? getMultilinePrefix(step.title) + line : line}
                    </tspan>
                  ))}
                </text>
              ) : (
                <text
                  className="value-loop-diagram__node-label"
                  x={labelX}
                  y={labelY}
                  textAnchor={node.labelAnchor}
                >
                  {step.title}
                </text>
              )}
            </g>
          )
        })}
      </svg>
    </div>
  )
}

export default ValueLoopDiagram
